import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { useCurrency } from '../CurrencyContext';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, useConsolidatedAccounts } from '../TransactionService';
import './AccountsManagerModal.css';

const ACCOUNT_TYPE_META = {
  debit:          { icon: '💳', label: 'Debit Card' },
  credit:         { icon: '💳', label: 'Credit Card' },
  bank:           { icon: '🏦', label: 'Bank Account' },
  cash:           { icon: '💵', label: 'Cash' },
  upi:            { icon: '📱', label: 'UPI' },
  prepaid_wallet: { icon: '👛', label: 'Wallet' },
  metro_card:     { icon: '🚇', label: 'Metro Card' },
  food_card:      { icon: '🍱', label: 'Food Card' },
  other:          { icon: '📦', label: 'Other' },
};

/**
 * AccountsManagerModal — single source of truth for reordering and hiding
 * accounts (instruments) from the Add Transaction picker. Used on both
 * desktop and mobile.
 *
 * Reordering is optimistic locally and persisted with a short debounce so
 * tapping up/down repeatedly doesn't fire a request per click. The hide
 * toggle saves immediately (one PATCH per click).
 *
 * Adding a brand-new instrument and editing field-level details (bank,
 * card last4, etc.) live in the existing `manage_instruments` mode in
 * AddTransaction. The modal exposes an "Add new instrument" button that
 * tells the parent to switch to that mode.
 */
export default function AccountsManagerModal({
  open,
  onClose,
  accounts = [],
  dashboardId,
  onAddNew,    // optional: parent-handled "+ Add Instrument" action
  onEdit,      // optional: parent-handled per-row edit
}) {
  const { currency } = useCurrency();
  const sym = currency.symbol;
  const queryClient = useQueryClient();

  const { data: consolidatedAccounts = [] } = useConsolidatedAccounts(dashboardId);
  const [showNewCA, setShowNewCA] = useState(false);
  const [newNameCA, setNewNameCA] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);

  // Local copy so the user sees instant feedback while reorder/hide writes
  // are in flight. Reset whenever the parent's accounts prop changes.
  const [localList, setLocalList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const reorderTimer = useRef(null);

  useEffect(() => {
    // Filter out the "__cash__" virtual placeholder; only manage real rows.
    const real = (accounts || []).filter((a) => !a._virtual);
    // Always show in display_order; fall back to created_at for ties.
    const sorted = [...real].sort((a, b) => {
      const oa = a.display_order ?? 0;
      const ob = b.display_order ?? 0;
      if (oa !== ob) return oa - ob;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    setLocalList(sorted);
  }, [accounts]);

  const groupedList = useMemo(() => {
    if (!localList.length) return [];
    const accountMap = {};
    localList.forEach(a => { accountMap[a.id] = a; });
    const usedIds = new Set();
    const result = [];
    (consolidatedAccounts || []).forEach(ca => {
      const members = (ca.account_ids || []).map(id => accountMap[id]).filter(Boolean);
      if (members.length > 0) {
        members.forEach(m => usedIds.add(m.id));
        result.push({ ...ca, isGroup: true, members });
      }
    });
    localList.forEach(a => {
      if (!usedIds.has(a.id)) result.push(a);
    });
    return result;
  }, [localList, consolidatedAccounts]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const move = (idx, dir) => {
    const next = [...localList];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setLocalList(next);
    scheduleOrderSave(next);
  };

  const scheduleOrderSave = (list) => {
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(() => persistOrder(list), 350);
  };

  const persistOrder = async (list) => {
    if (!dashboardId) return;
    setSavingOrder(true);
    setErrorMsg('');
    try {
      await api.post('/api/accounts/reorder', {
        dashboard_id: dashboardId,
        order: list.map((a) => a.id),
      });
      // Invalidate the accounts cache so other open views (Add Transaction
      // picker, dashboard summaries) pick up the new order on next render.
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  const toggleHidden = async (acc) => {
    const nextHidden = !acc.hidden_from_add;
    // Optimistic local update
    setLocalList((prev) => prev.map((a) => (a.id === acc.id ? { ...a, hidden_from_add: nextHidden } : a)));
    setErrorMsg('');
    try {
      await api.patch(`/api/accounts/${acc.id}`, { hidden_from_add: nextHidden });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(dashboardId) });
    } catch (err) {
      // Revert on failure
      setLocalList((prev) => prev.map((a) => (a.id === acc.id ? { ...a, hidden_from_add: !nextHidden } : a)));
      setErrorMsg(err?.response?.data?.error || 'Failed to update');
    }
  };

  // Flush any pending debounced reorder before close
  useEffect(() => {
    if (!open && reorderTimer.current) {
      clearTimeout(reorderTimer.current);
      reorderTimer.current = null;
    }
  }, [open]);

  const realCount = localList.length;
  const hiddenCount = useMemo(
    () => localList.filter((a) => a.hidden_from_add).length,
    [localList],
  );

  if (!open) return null;

  return (
    <div className="amm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div className="amm-modal" role="dialog" aria-modal="true" aria-label="Manage Instruments">
        <div className="amm-head">
          <div>
            <div className="amm-title">Manage Instruments</div>
            <div className="amm-sub">
              {realCount} total
              {hiddenCount > 0 && ` · ${hiddenCount} hidden from Add`}
              {savingOrder && ' · saving…'}
            </div>
          </div>
          <button type="button" className="amm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {errorMsg && <div className="amm-err">{errorMsg}</div>}

        {realCount === 0 ? (
          <div className="amm-empty">
            <div className="amm-empty-ico">🏦</div>
            <div className="amm-empty-title">No instruments yet</div>
            <div className="amm-empty-sub">Add your first card, bank account or wallet to get started.</div>
          </div>
        ) : (
          <ul className="amm-list">
            {groupedList.map((item, idx) => {
              if (item.isGroup) {
                return (
                  <React.Fragment key={item.id}>
                    <li className="amm-group-header">
                      <div className="amm-group-main">
                        <div className="amm-group-icon">📁</div>
                        <div className="amm-group-name">{item.name}</div>
                      </div>
                    </li>
                    {item.members.map(acc => {
                      const meta = ACCOUNT_TYPE_META[acc.account_type] || ACCOUNT_TYPE_META.other;
                      const bal = parseFloat(acc.current_balance || 0);
                      const isOwed = acc.mechanism === 'pay_later';
                      const hidden = !!acc.hidden_from_add;
                      const realIdx = localList.findIndex(a => a.id === acc.id);
                      return (
                        <li key={acc.id} className={`amm-row${hidden ? ' is-hidden' : ''}`}>
                          <div className="amm-reorder">
                            <button
                              type="button"
                              className="amm-arrow"
                              onClick={() => move(realIdx, -1)}
                              disabled={realIdx === 0}
                              aria-label="Move up"
                            >▲</button>
                            <span className="amm-rank">{realIdx + 1}</span>
                            <button
                              type="button"
                              className="amm-arrow"
                              onClick={() => move(realIdx, +1)}
                              disabled={realIdx === localList.length - 1}
                              aria-label="Move down"
                            >▼</button>
                          </div>
                          <div className="amm-icon indented">{meta.icon}</div>
                          <div className="amm-main">
                            <div className="amm-name">{acc.name}</div>
                            <div className="amm-meta">
                              <span className="amm-type">{meta.label}</span>
                              {acc.bank_name && <span className="amm-pill">{acc.bank_name}</span>}
                            </div>
                          </div>
                          <div className={`amm-bal ${isOwed ? 'owed' : ''}`}>
                            {isOwed ? '-' : ''}{sym}{Math.abs(bal).toLocaleString()}
                          </div>
                          <div className="amm-actions">
                            <button type="button" className={`amm-toggle${hidden ? ' active' : ''}`} onClick={() => toggleHidden(acc)}>
                              {hidden ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                  <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              )}
                            </button>
                            {onEdit && (
                              <button type="button" className="amm-edit" onClick={() => onEdit(acc)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </React.Fragment>
                );
              }

              const acc = item;
              const meta = ACCOUNT_TYPE_META[acc.account_type] || ACCOUNT_TYPE_META.other;
              const bal = parseFloat(acc.current_balance || 0);
              const isOwed = acc.mechanism === 'pay_later';
              const hidden = !!acc.hidden_from_add;
              // For flat list reordering, we need to find the REAL index in localList
              const realIdx = localList.findIndex(a => a.id === acc.id);
              
              return (
                <li key={acc.id} className={`amm-row${hidden ? ' is-hidden' : ''}`}>
                  <div className="amm-reorder">
                    <button
                      type="button"
                      className="amm-arrow"
                      onClick={() => move(realIdx, -1)}
                      disabled={realIdx === 0}
                      aria-label="Move up"
                    >▲</button>
                    <span className="amm-rank">{realIdx + 1}</span>
                    <button
                      type="button"
                      className="amm-arrow"
                      onClick={() => move(realIdx, +1)}
                      disabled={realIdx === localList.length - 1}
                      aria-label="Move down"
                    >▼</button>
                  </div>

                  <div className="amm-icon">{meta.icon}</div>

                  <div className="amm-main">
                    <div className="amm-name">{acc.name}</div>
                    <div className="amm-meta">
                      <span className="amm-type">{meta.label}</span>
                      {acc.bank_name && <span className="amm-pill">{acc.bank_name}</span>}
                    </div>
                  </div>

                  <div className={`amm-bal ${isOwed ? 'owed' : ''}`}>
                    {isOwed ? '-' : ''}{sym}{Math.abs(bal).toLocaleString()}
                  </div>

                  <div className="amm-actions">
                    <button
                      type="button"
                      className={`amm-toggle${hidden ? ' active' : ''}`}
                      onClick={() => toggleHidden(acc)}
                    >
                      {hidden ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>

                    {onEdit && (
                      <button
                        type="button"
                        className="amm-edit"
                        onClick={() => onEdit(acc)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="amm-foot">
          {onAddNew && (
            <button type="button" className="amm-add" onClick={() => { onAddNew(); }}>
              <span className="amm-add-plus">+</span> Add Instrument
            </button>
          )}
          <button type="button" className="amm-add amm-add-collection" onClick={() => setShowNewCA(true)}>
            <span className="amm-add-plus">+</span> Create Collection
          </button>
        </div>

        {showNewCA && (
          <div className="amm-ca-overlay">
            <div className="amm-ca-form">
              <div className="amm-ca-form-head">
                <div className="amm-ca-form-title">Create Collection</div>
                <button className="amm-ca-form-close" onClick={() => setShowNewCA(false)}>✕</button>
              </div>
              <input 
                className="amm-ca-input"
                placeholder="Collection Name (e.g. Axis Total)"
                value={newNameCA}
                onChange={e => setNewNameCA(e.target.value)}
                autoFocus
              />
              <div className="amm-ca-picker">
                <div className="amm-ca-picker-label">Include Accounts:</div>
                {localList.map(acc => (
                  <label key={acc.id} className="amm-ca-checkbox-row">
                    <input 
                      type="checkbox"
                      checked={selectedAccountIds.includes(acc.id)}
                      onChange={e => {
                        if (e.target.checked) setSelectedAccountIds([...selectedAccountIds, acc.id]);
                        else setSelectedAccountIds(selectedAccountIds.filter(id => id !== acc.id));
                      }}
                    />
                    <span>{acc.name}</span>
                  </label>
                ))}
              </div>
              <div className="amm-ca-form-btns">
                <button className="amm-ca-save" disabled={!newNameCA || selectedAccountIds.length === 0} onClick={async () => {
                  if (!newNameCA || selectedAccountIds.length === 0) return;
                  await api.post('/api/consolidated-accounts', {
                    dashboard_id: dashboardId,
                    name: newNameCA,
                    account_ids: selectedAccountIds
                  });
                  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.consolidatedAccounts(dashboardId) });
                  setShowNewCA(false);
                  setNewNameCA('');
                  setSelectedAccountIds([]);
                }}>Save Collection</button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
}
